import {
  connectQuickbooksService,
  disconnectQuickbooksService,
  sendQuickbooksData,
  syncQuickbooksData,
} from "@/service/quickbooks.service";
import { successToast } from "@/utils/toast";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "@store";

interface QuickbookConnectState {
  isLoading: boolean;
  syncLoading: boolean;
  error: string | null;
  isConnected: boolean;
  params: {
    code: string;
    state: string;
    realmId: string;
  };
  accessToken: string;
  refreshToken: string;
  expires_in: string;
  token_type: string;
}

const initialState: QuickbookConnectState = {
  isLoading: false,
  syncLoading: false,
  error: null,
  isConnected: false,
  params: {
    code: "",
    state: "",
    realmId: "",
  },
  expires_in: "",
  accessToken: "",
  refreshToken: "",
  token_type: "",
};

// Async Actions
export const connectQuickbooks = createAsyncThunk(
  "quickbookConnect/connectQuickbooks",
  async (_, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));

    const data=  await connectQuickbooksService();
    if(data?.data?.auth_url){
      window.location.href= data?.data?.auth_url;
    } 
    } catch (error: any) {
      console.log(error);
      const message =
        error.response?.data?.message || "Failed to connect quickbooks";
      // errorToast(message, "");
      dispatch(setError(message));
      throw error;
    }
  }
);

export const disconnectQuickbooks = createAsyncThunk(
  "quickbookConnect/disconnectQuickbooks",
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setIsLoading(true));

      const state = getState() as RootState;

      await disconnectQuickbooksService({
        refreshToken: state.quickbookConnect.refreshToken,
      });

      return true;
    } catch (error: any) {
      console.log(error);
      const message =
        error.response?.data?.message || "Failed to disconnect quickbooks";
      // errorToast(message, "");
      dispatch(setError(message));
      throw error;
    }
  }
);

interface SendQuickbooksDataParams {
  code: string;
  state: string;
  realmId: string;
}

export const sendQuickbooksDataAction = createAsyncThunk(
  "quickbookConnect/sendQuickbooksData",
  async (params: SendQuickbooksDataParams, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));

      const { data }: any = await sendQuickbooksData({
        code: params.code,
        state: params.state,
        realmId: params.realmId,
      });

      return { data, params };
    } catch (error: any) {
      console.log(error);
      const message =
        error.response?.data?.message || "Failed to send quickbooks data";
      // errorToast(message, "");
      dispatch(setError(message));
      throw error;
    }
  }
);

export const syncQuickbooksDataAction = createAsyncThunk(
  "quickbookConnect/syncQuickbooksData",
  async (_, { dispatch }) => {
    try {
      dispatch(setSyncLoading(true));

      const { data }: any = await syncQuickbooksData();

      successToast("Quickbooks sync completed", data.message);

      return true;
    } catch (error: any) {
      console.log(error);

      dispatch(setIsConnected(false));
      dispatch(setSyncLoading(false));
      dispatch(setError(null));
      dispatch(setParams({ code: "", state: "", realmId: "" }));

      const message =
        error.response?.data?.message || "Failed to sync quickbooks data";
      // errorToast(message, "");
      dispatch(setError(message));
      throw error;
    }
  }
);

const quickbookConnectSlice = createSlice({
  name: "quickbookConnect",
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setSyncLoading: (state, action) => {
      state.syncLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setIsConnected: (state, action) => {
      state.isConnected = action.payload;
    },
    setParams: (state, action) => {
      state.params = action.payload;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
    },
    resetQuickbookConnect: (state) => {
      state.isLoading = false;
      state.syncLoading = false;
      state.error = null;
      state.isConnected = false;
      state.params = { code: "", state: "", realmId: "" };
      state.accessToken = "";
      state.refreshToken = "";
      state.expires_in = "";
      state.token_type = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(connectQuickbooks.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(connectQuickbooks.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(connectQuickbooks.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to connect quickbooks";
    });

    builder.addCase(disconnectQuickbooks.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(disconnectQuickbooks.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(disconnectQuickbooks.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to disconnect quickbooks";
    });

    builder.addCase(sendQuickbooksDataAction.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(sendQuickbooksDataAction.fulfilled, (state, action) => {
      state.isConnected = true;
      state.params = action.payload.params;
      // state.accessToken = action.payload.data.access_token;
      // state.refreshToken = action.payload.data.refresh_token;
      state.expires_in = action.payload.data.expires_in;
      state.token_type = action.payload.data.token_type;
    });
    builder.addCase(sendQuickbooksDataAction.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to send quickbooks data";
    });

    builder.addCase(syncQuickbooksDataAction.pending, (state) => {
      state.syncLoading = true;
      state.error = null;
    });
    builder.addCase(syncQuickbooksDataAction.fulfilled, (state) => {
      state.syncLoading = false;
    });
    builder.addCase(syncQuickbooksDataAction.rejected, (state, action) => {
      state.syncLoading = false;
      state.error = action.error.message || "Failed to sync quickbooks data";
    });
  },
});

export const {
  setIsLoading,
  setError,
  setIsConnected,
  setParams,
  setAccessToken,
  setRefreshToken,
  setSyncLoading,
  resetQuickbookConnect,
} = quickbookConnectSlice.actions;

export const selectIsLoading = (state: RootState) =>
  state.quickbookConnect.isLoading;
export const selectSyncLoading = (state: RootState) =>
  state.quickbookConnect.syncLoading;
export const selectError = (state: RootState) => state.quickbookConnect.error;
export const selectIsConnected = (state: RootState) =>
  state.quickbookConnect.isConnected;
export const selectParams = (state: RootState) => state.quickbookConnect.params;

export default quickbookConnectSlice.reducer;
